import { formatUnits, parseUnits } from 'viem'
import type { Address } from 'viem'
import type { PoolView, TokenInfo } from './chain'
import { FEE_ON_TRANSFER_TOKENS, WBNB, lc } from './dexes'

/** The pure part of the trade panel: which token goes in and out, amounts, presets and the reasons a trade cannot be signed yet. No network, no React. */

export type Side = 'buy' | 'sell'

export interface Legs {
  side: Side
  project: TokenInfo
  /** The pool's other token. */
  quote: TokenInfo
  tokenIn: TokenInfo
  tokenOut: TokenInfo
  nativeIn: boolean
  nativeOut: boolean
  /** What the wallet sees: "BNB" for the native leg of a WBNB pool, otherwise the token's symbol. */
  inLabel: string
  outLabel: string
  quoteLabel: string
}

/** null when the pool does not hold the project token at all. BNB is used (not WBNB) for the WBNB side of a pool: that is what a wallet holds. */
export function legsOf(view: PoolView, projectToken: Address, side: Side): Legs | null {
  const p = lc(projectToken)
  let project: TokenInfo
  let quote: TokenInfo
  if (lc(view.token0.address) === p) [project, quote] = [view.token0, view.token1]
  else if (lc(view.token1.address) === p) [project, quote] = [view.token1, view.token0]
  else return null
  const quoteIsWbnb = lc(quote.address) === lc(WBNB)
  const quoteLabel = quoteIsWbnb ? 'BNB' : quote.symbol
  const buy = side === 'buy'
  return {
    side,
    project,
    quote,
    tokenIn: buy ? quote : project,
    tokenOut: buy ? project : quote,
    nativeIn: buy && quoteIsWbnb,
    nativeOut: !buy && quoteIsWbnb,
    inLabel: buy ? quoteLabel : project.symbol,
    outLabel: buy ? project.symbol : quoteLabel,
    quoteLabel,
  }
}

/** "12.5" or "12,5" to base units; null for anything that is not a positive decimal with no more digits than the token has. */
export function parseAmount(text: string, decimals: number): bigint | null {
  const t = text.trim().replace(',', '.')
  if (!/^\d*\.?\d*$/.test(t) || t === '' || t === '.') return null
  const [, frac = ''] = t.split('.')
  if (frac.length > decimals) return null
  try {
    const v = parseUnits(t.startsWith('.') ? `0${t}` : t, decimals)
    return v > 0n ? v : null
  } catch {
    return null
  }
}

/** Minimum received: output x (1 - slippage), rounded down, as the routers enforce it. `slippageBips` is the app's global setting (50 = 0.5 %). */
export function minOutOf(amountOut: bigint, slippageBips: number): bigint {
  const bips = BigInt(Math.max(0, Math.min(5000, Math.round(slippageBips))))
  return (amountOut * (10_000n - bips)) / 10_000n
}

export interface Preset {
  label: string
  kind: 'percent'
  value: string
}

/** Shares of the wallet's balance of the token that goes in, as the Swap page offers them: 25 %, 50 %, 75 % and Max. Free typing stays; a fixed amount would be useless to a wallet with a dollar in it. */
export function presetsFor(_legs?: Legs): Preset[] {
  return [
    { label: '25%', kind: 'percent', value: '25' },
    { label: '50%', kind: 'percent', value: '50' },
    { label: '75%', kind: 'percent', value: '75' },
    { label: 'Max', kind: 'percent', value: '100' },
  ]
}

/** What a wallet with none of the input token can do: where to get it. null = nothing to offer (the Sell side offers "Buy first" in the panel itself). */
export function getTokenLink(legs: Legs): { label: string; href: string; external: boolean } | null {
  if (legs.side === 'sell') return null
  if (legs.nativeIn) return { label: 'Get BNB', href: 'https://www.binance.com/en/price/bnb', external: true }
  return { label: `Get ${legs.quote.symbol}`, href: `/swap?outputCurrency=${legs.quote.address}&chainId=56`, external: false }
}

const REJECTION_TEXT = /user (rejected|denied|cancell?ed)|rejected the request|request (was )?rejected|denied transaction signature|transaction was rejected|signature was rejected/i
/**
 * True when the wallet's owner declined. viem wraps the provider's 4001 in a TransactionExecutionError whose `name` says nothing about it (the words are in
 * `shortMessage` and in the `cause`), so the whole chain is read; a revert reason that merely contains "denied" is not a refusal by the person.
 */
export function isUserRejection(e: unknown): boolean {
  let cur: unknown = e
  for (let depth = 0; depth < 6 && cur !== null && cur !== undefined; depth++) {
    if (typeof cur === 'string') return REJECTION_TEXT.test(cur)
    if (typeof cur !== 'object') return false
    const o = cur as { name?: unknown; code?: unknown; shortMessage?: unknown; message?: unknown; cause?: unknown }
    if (o.code === 4001 || o.code === 'ACTION_REJECTED') return true
    if (typeof o.name === 'string' && /UserRejected|ActionRejected|UserDenied|UserCancel/i.test(o.name)) return true
    if ([o.shortMessage, o.message].some((m) => typeof m === 'string' && REJECTION_TEXT.test(m))) return true
    cur = o.cause
  }
  return false
}
/** Text for an amount of base units, trimmed to something readable (6 decimals at most). */
export function fmtUnits(v: bigint, decimals: number, max = 6): string {
  const s = formatUnits(v, decimals)
  const [i, f = ''] = s.split('.')
  const cut = f.slice(0, max).replace(/0+$/, '')
  return cut ? `${Number(i).toLocaleString('en-US')}.${cut}` : Number(i).toLocaleString('en-US')
}

/** A share of the balance, as an amount string the input takes. The native leg keeps a little BNB back for gas when the whole balance is asked for. */
export function shareOf(balance: bigint, percent: number, decimals: number, reserve = 0n): string {
  const spendable = balance > reserve ? balance - reserve : 0n
  const v = percent >= 100 ? spendable : (spendable * BigInt(percent)) / 100n
  return formatUnits(v, decimals)
}

export const GAS_RESERVE_WEI = 3_000_000_000_000_000n // 0.003 BNB

export type Blocker =
  | 'no_pool'
  | 'not_in_pool'
  | 'fee_on_transfer'
  | 'no_amount'
  | 'quote_loading'
  | 'quote_failed'
  | 'balance'
  | 'route'
  | 'impact_blocked'
  | 'impact_confirm'
  | 'expired'

/** Thin-pool and fee-token warnings that do not block: shown as text. */
export function isFeeOnTransfer(token: Address): boolean {
  return FEE_ON_TRANSFER_TOKENS.has(lc(token))
}

export type LiquidityLevel = 'ok' | 'thin' | 'very_thin'
/** Neutral, exchange-blind thresholds on the pool's USD liquidity (when known). */
export function liquidityLevel(usd: number | null | undefined): LiquidityLevel {
  if (usd === null || usd === undefined || !Number.isFinite(usd)) return 'ok'
  if (usd < 2_000) return 'very_thin'
  if (usd < 20_000) return 'thin'
  return 'ok'
}
